/**
 * 案件が触った要素の索引 — 案件の変更の行から差分の印を畳む。
 *
 * **印の出どころは案件の変更の 1 本に絞る**（決定 7）。業務全体像（`getBusinessMap`）が返す
 * のは「master」か「master ⊕ 案件」の姿だけで、印は持たない。master と案件適用後を 2 回
 * 読んで突き合わせる形にすると、同じ印が 2 本の出どころから作られる。
 *
 * 印の語彙と色は `SpecDiffState.tsx` が持つ（画面をまたいでも同じ「追加」が同じ色で出る）。
 * ここが決めるのは**どのノードがどの状態か**だけである。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 案件の変更の行から、ノードの id ごとの差分の状態（追加 / 変更 / 削除）の索引を畳む。
 * @graph-connects SpecDiffState [references] 差分の状態の語彙
 */
import type { PjChangeRow } from '@cortex/domain-spec-console';

import type { SpecDiffState } from '../components/spec/SpecDiffState.js';

/**
 * ノードの id から差分の状態を引く索引。
 *
 * 触っていないノードは**キーごと持たない**。`unchanged` を全ノードぶん詰めると、索引の
 * 大きさがグラフ全体になり、「触ったものを拾う」という索引の用途が消える。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 案件が触ったノードの id から差分の状態を引く索引
 * @graph-connects none
 */
export type PjTouchedIndex = ReadonlyMap<number, SpecDiffState>;

/** 何も触っていない案件（と案件を選んでいないとき）の索引。 @graph-connects none */
export const EMPTY_PJ_TOUCHED_INDEX: PjTouchedIndex = new Map();

/**
 * 案件の変更 1 行の差分の状態を決める。
 *
 * | 行の前後 | 状態 |
 * |---|---|
 * | 変更前が無い | 追加（その案件が作った） |
 * | 変更後が無い | 削除（その案件が消した） |
 * | 両方ある | 変更 |
 *
 * 前後とも無い行は作られない（変化の無いノードは行を作らない — 設計書 §5 手順 5）。
 * それでも来たら**変更として扱わない**で `null` を返し、索引に入れない。前後が両方無い行は
 * 何が起きたか読めないので、印を付けると読み手に嘘の手掛かりを渡すことになる。
 *
 * @param row 案件の変更 1 行
 * @returns 差分の状態。読めない行は `null`
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 案件の変更 1 行の前後から差分の状態（追加 / 変更 / 削除）を決める。
 * @graph-connects none
 */
export function pjChangeDiffState(row: PjChangeRow): SpecDiffState | null {
  if (row.before === null && row.after === null) return null;
  if (row.before === null) return 'created';
  if (row.after === null) return 'deleted';
  return 'updated';
}

/**
 * 案件の変更の行から索引を畳む。
 *
 * 行は（案件, ノード）で 1 つしか無いので、古い順に並べ替えて上書きする手順は要らない。
 *
 * @param rows 案件の変更の行
 * @returns ノードの id から差分の状態を引く索引
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 案件の変更の行を、ノードの id から差分の状態を引く索引へ畳む。
 * @graph-connects none
 */
export function buildPjTouchedIndex(rows: readonly PjChangeRow[]): PjTouchedIndex {
  const index = new Map<number, SpecDiffState>();
  for (const row of rows) {
    const state = pjChangeDiffState(row);
    if (state !== null) index.set(row.nodeId, state);
  }
  return index;
}

/**
 * 索引からそのノードの差分の状態を引く。触っていなければ `unchanged`。
 *
 * @param index 索引
 * @param nodeId ノードの id
 * @returns 差分の状態
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 索引からノード 1 件の差分の状態を引く（触っていなければ変更なし）。
 * @graph-connects none
 */
export function pjTouchedState(index: PjTouchedIndex, nodeId: number): SpecDiffState {
  return index.get(nodeId) ?? 'unchanged';
}
